import {
	Component,
	EventEmitter,
	forwardRef,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { coerceBoolean } from '@app-shared/decorators/coerce-boolean.decorator';
import { MatAutocomplete } from '@angular/material/autocomplete';

@Component({
	selector: 'app-search-input',
	templateUrl: './search-input.component.html',
	styleUrls: ['./search-input.component.scss'],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => SearchInputComponent),
			multi: true
		}
	]
})
export class SearchInputComponent implements OnInit, OnDestroy, OnChanges, ControlValueAccessor {
	@Input() placeholder = 'Search';
	@Input() label: string | null = null;
	@Input() debounceMs = 300;
	@Input() value = '';
	@Input() appearance: 'fill' | 'outline' | 'standard' | 'legacy' = 'fill';
	@Input() showButton = true;
	@Input() onlyOnEnterOrButton = true;
	@Input() prefixIcon: string | null = null;
	@Input() suffixIcon: string | null = null;
	@Input() matAutocomplete: MatAutocomplete | null = null;
	@Input() spellcheck: boolean | null = null;
	@coerceBoolean() @Input() debounceOnTyping = false;
	@coerceBoolean() @Input() showClear = false;

	@Output() search = new EventEmitter<string>();
	@Output() clear = new EventEmitter<void>();

	control = new FormControl('');
	private sub = new Subscription();

	private onChange: (v: any) => void = () => {};
	private onTouched: () => void = () => {};
	disabled = false;

	get hasSuffixActions(): boolean {
		return (!!this.showClear && !!this.control.value) || !!this.showButton;
	}

	ngOnInit(): void {
		this.control.setValue(this.value || '', { emitEvent: false });

		const csub = this.control.valueChanges.pipe(distinctUntilChanged()).subscribe(v => this.onChange(v));
		this.sub.add(csub);

		const shouldDebounceOnTyping = !!this.debounceOnTyping && !this.onlyOnEnterOrButton;
		if (shouldDebounceOnTyping) {
			const s = this.control.valueChanges
				.pipe(debounceTime(this.debounceMs), distinctUntilChanged())
				.subscribe(v => this.search.emit(v ? v.trim() : ''));
			this.sub.add(s);
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (!('value' in changes)) return;

		const next = changes['value'].currentValue ?? '';
		if (next !== this.control.value) {
			this.control.setValue(next, { emitEvent: false });
		}
	}

	ngOnDestroy(): void {
		this.sub.unsubscribe();
	}

	onEnter(): void {
		const v = this.control.value ? this.control.value.trim() : '';
		this.onTouched();
		this.onChange(v);
		this.search.emit(v);
	}

	onClear(): void {
		this.control.setValue('');
		this.onTouched();
		this.onChange('');
		this.clear.emit();
		this.search.emit('');
	}

	writeValue(obj: any): void {
		this.control.setValue(obj, { emitEvent: false });
	}

	registerOnChange(fn: any): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: any): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
		if (isDisabled) {
			this.control.disable({ emitEvent: false });
		} else {
			this.control.enable({ emitEvent: false });
		}
	}
}
